<script setup lang="ts">
// import type { EntryInterfaceTypeFragment, MediaFragment, NewsThumbFragment } from '#graphql-operations'
import type { MediaFragment, NewsThumbFragment } from '#graphql-operations'

interface CardNewsProps {
  data: NewsThumbFragment
  featured?: boolean
}

const props = defineProps<CardNewsProps>()

const thumbMedia = computed(() => props.data.thumbMedia as MediaFragment[])
const colorScheme = useColorScheme(() => props.data.colorScheme)
const featured = computed(() => props.featured && props.data.featured)
// const categories = computed(() => props.data.newsCategory as EntryInterfaceTypeFragment[])
</script>

<template>
  <article class="cardNews group group-buttonLink h-full" :class="[colorScheme, { featured }]">
    <Anchor class="h-full block" :class="{ 'lg:h-auto': !featured }" :to="data.url">
      <figure class="rounded-2 grid grid-cols-2 h-full relative of-hidden" :class="{ 'lg:h-auto lg:grid-cols-1': !featured }">
        <div class="of-hidden lg:order-last">
          <CraftMedia
            v-for="media in thumbMedia"
            :key="media.id"
            class="motion-relaxed w-full object-cover lt-lg:size-full group-focus-within:scale-105 group-hover-media-mouse:scale-105"
            :class="featured ? 'aspect-0.68 lg:aspect-1.21' : 'aspect-0.68 lg:aspect-0.8'"
            :media
            :img-props="{ alt: data.title }"
          />
          <div
            v-if="thumbMedia.length === 0"
            class="bg-$primary-color w-full"
            :class="featured ? 'aspect-0.68 lg:aspect-1.21' : 'aspect-0.68 lg:aspect-0.8'"
          />
        </div>
        <figcaption
          class="c-$color-scheme-foreground p-4 motion-base bg-$color-scheme-background lg:p-7.5"
          :class="{
            'lg:opacity-0 lg:inset-0 lg:absolute lg:z-1 group-focus-within:opacity-100 group-hover-media-mouse:opacity-100': !featured,
          }"
        >
          <div class="text-center flex flex-col h-full justify-center of-hidden space-y-2 lg:space-y-5">
            <div class="mt-auto space-y-2 lg:space-y-5" :class="{ 'flex-1': !featured }">
              <h3 class="heading typo-serif-3">
                <Text v-if="featured && data.heading" :html="data.heading" />
                <Text v-else :text="data.title" />
              </h3>
            </div>
            <div class="content flex flex-1 flex-col justify-center lt-lg:mb-0 lg:space-y-1.5">
              <Text v-if="data.date" :text="data.date" class="typo-sans-1" />
              <Text v-if="data.excerptText" :text="data.excerptText" class="desktop-only typo-serif-1" />
            </div>
            <div class="mt-auto">
              <ButtonLink as="span" :title="$t('read_more')" />
            </div>
          </div>
        </figcaption>
      </figure>
    </Anchor>
  </article>
</template>

<style scoped>
.cardNews {
  height: 100%;

  &:not(.featured) {
    @screen lg {
      height: auto;
    }
  }
}

.cardNews:not(.featured) .content {
  @screen lg {
    max-width: var(--container-lg);
    margin: 0 auto;
  }
}

.cardNews.featured .content {
  @screen lg {
    margin-bottom: 0;
    flex: none;

    width: 100%;
    margin-inline: auto;
    max-width: 60%;
  }
}
</style>
